import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sparkles } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import { IMAGES, PILLARS, JOURNEY } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const icons = [Leaf, SunriseCircle, Sparkles];

export default function Method() {
  return (
    <div data-testid="method-page" className="bg-brand-cream">
      {/* HERO — How Transformation Happens */}
      <section className="pt-40 md:pt-52 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <FadeUp><span className="overline">The Brighter Tomorrows Method</span></FadeUp>
            <FadeUp delay={0.1}>
              <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-brand-forest tracking-tight leading-[0.95] mt-6">
                How Transformation<br />Happens
              </h1>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="md:col-span-4">
            <p className="font-serif italic text-2xl md:text-3xl text-brand-forest leading-snug">
              We don't start by changing your life.<br />We start by understanding it.
            </p>
          </FadeUp>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-10">
          <FadeUp delay={0.25}>
            <p className="text-brand-inkSoft text-lg md:text-xl leading-relaxed max-w-3xl">
              The Brighter Tomorrows Method meets you where you are and moves at the pace of true
              integration — not pressure, perfection, or someone else's timeline.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* JOURNEY — 8 steps */}
      <section className="pb-24 md:pb-32" data-testid="method-journey">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">The Journey · Meet to Become</span></FadeUp>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" stagger={0.07}>
            {JOURNEY.map((s) => (
              <StaggerItem key={s.n}>
                <div
                  data-testid={`method-journey-${s.n}`}
                  className="group h-full bg-brand-beige/40 hover:bg-brand-forest rounded-3xl p-7 transition-colors duration-500"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif text-4xl text-brand-sage group-hover:text-brand-goldBright transition-colors duration-500">{s.n}</span>
                    <h3 className="font-serif text-2xl md:text-3xl text-brand-forest group-hover:text-brand-cream tracking-tight transition-colors duration-500">{s.title}</h3>
                  </div>
                  <p className="text-brand-inkSoft group-hover:text-brand-cream/75 leading-relaxed mt-4 text-sm transition-colors duration-500">{s.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* READINESS — how we know when you're ready */}
      <section className="bg-brand-forest text-brand-cream py-28 md:py-40 relative grain overflow-hidden" data-testid="method-readiness">
        <div className="max-w-3xl mx-auto px-6 md:px-10 relative z-10">
          <FadeUp><span className="overline text-brand-goldBright">Integration over pressure</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-cream tracking-tight mt-5 leading-[1.05]">
              How do we know when you're ready for what's next?
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="font-serif italic text-2xl md:text-3xl text-brand-goldBright mt-8 leading-snug">
              We don't force progression. We look for integration.
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <p className="text-brand-cream/85 text-lg leading-relaxed mt-8">
              When awareness becomes something you can recognize on your own, we build. When a new
              behavior becomes sustainable, we add to it. And when you feel stuck, we don't push
              harder — we explore what's underneath the resistance.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-10 pl-6 border-l-2 border-brand-goldBright/50">
              <p className="text-brand-cream/70 text-lg leading-relaxed">
                Because the goal isn't to move through a program.
              </p>
              <p className="font-serif text-2xl md:text-3xl text-brand-cream mt-3 leading-snug">
                It's to build a life you no longer have to force yourself to maintain.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee items={["Meet", "Validate", "Shift", "Choose", "Practice", "Become"]} />

      {/* PILLARS — moved to bottom, smaller boxes */}
      <section className="py-28 md:py-36" data-testid="method-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Woven through every step</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-forest tracking-tight mt-5 max-w-2xl leading-[1.02]">
              The Six Pillars of Lasting Change™
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-brand-inkSoft text-lg leading-relaxed mt-6 max-w-xl">
              Each pillar is woven together and tailored to your unique story, your pace, and your
              purpose — transformation built to last.
            </p>
          </FadeUp>

          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14" stagger={0.06}>
            {PILLARS.map((p, i) => {
              const Icon = icons[i % icons.length];
              return (
                <StaggerItem key={p.title}>
                  <div
                    data-testid={`method-pillar-${i}`}
                    className="group h-full bg-brand-beige/40 hover:bg-brand-forest p-6 rounded-2xl transition-colors duration-500"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <Icon className="text-brand-gold group-hover:text-brand-goldBright transition-colors" strokeWidth={1.2} size={24} />
                      <span className="font-serif text-brand-sage group-hover:text-brand-cream/40 text-2xl transition-colors">0{i + 1}</span>
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl text-brand-forest group-hover:text-brand-cream transition-colors tracking-tight">{p.title}</h3>
                    <p className="text-brand-inkSoft group-hover:text-brand-cream/75 leading-relaxed mt-2 text-sm transition-colors">{p.body}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* image + cta */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden" data-testid="method-cta">
        <img src={IMAGES.interior} alt="A woman in serene natural light" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-forest/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-forest via-brand-forest/40 to-brand-forest/70" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-cream tracking-tight max-w-3xl leading-[1.05] [text-shadow:0_2px_24px_rgba(15,26,22,0.6)]">
              Weave wellness into a living whole.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/#discovery-call"
                data-testid="method-discovery-button"
                className="group inline-flex items-center gap-3 bg-brand-goldBright text-brand-forest px-10 py-4 rounded-full text-sm tracking-wide font-medium hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300"
              >
                Book a Discovery Call
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/packages"
                data-testid="method-cta-button"
                className="group inline-flex items-center gap-3 border border-brand-cream/40 text-brand-cream px-10 py-4 rounded-full text-sm tracking-wide hover:bg-brand-cream/10 transition-colors duration-300"
              >
                View investment options
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
